import React, { useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import { requirementsApi } from '../../services/api';
import { attributesToRows, rowsToAttributes } from '../../utils/attributeChanges';
import { saveWithConflictPrompt } from '../../utils/conflict';
import { resolveIdDisplay, stripIdPrefix } from '../../utils/itemId';
import { statusOf, approvalToneOf } from '../../utils/statusTone';
import { itemCardZ } from '../../utils/zLayers';
import { usePref } from '../../services/prefs';
import { useItemCards } from './ItemCardContext';
import ItemTooltip from './ItemTooltip';
import UnifiedContainerSelector from '../tree/UnifiedContainerSelector';

const isMobile = () => typeof window !== 'undefined' && window.innerWidth <= 640;

// Resize constraints (px). Any edge or corner grabs a resize; left/top edges
// also shift x/y so the opposite corner stays anchored under the pointer.
const MIN_W = 320, MIN_H = 220;
const EDGES = [
  { dir: 'n', cursor: 'ns-resize', style: { top: -4, left: 6, right: 6, height: 8 } },
  { dir: 's', cursor: 'ns-resize', style: { bottom: -4, left: 6, right: 6, height: 8 } },
  { dir: 'e', cursor: 'ew-resize', style: { right: -4, top: 6, bottom: 6, width: 8 } },
  { dir: 'w', cursor: 'ew-resize', style: { left: -4, top: 6, bottom: 6, width: 8 } },
  { dir: 'ne', cursor: 'nesw-resize', style: { top: -4, right: -4, width: 12, height: 12 } },
  { dir: 'nw', cursor: 'nwse-resize', style: { top: -4, left: -4, width: 12, height: 12 } },
  { dir: 'se', cursor: 'nwse-resize', style: { bottom: -4, right: -4, width: 12, height: 12 } },
  { dir: 'sw', cursor: 'nesw-resize', style: { bottom: -4, left: -4, width: 12, height: 12 } },
];

/**
 * One advanced-tooltip card.
 *  - unpinned (preview): anchored, dismiss-on-leave, read/edit; pins itself the
 *    moment you interact (focus a field or hit 📌).
 *  - pinned: draggable window on desktop, modal on mobile, persistent, recursive.
 */
export default function ItemCardWindow({ win, zRank }) {
  // #627: `zRank` is the card's bounded position within the open set, supplied by
  // ItemCardLayer. `win.z` (the raw monotonic counter) is still the ordering key and
  // is deliberately NOT painted — it grows without bound. Defaults to `z` so a direct
  // mount without the prop (older tests) still renders in a sane band via the clamp.
  const { key, item: seed, x, y, z, pinned, w, h } = win;
  const { closeWindow, focusWindow, moveWindow, resizeWindow, pinWindow, navigate } = useItemCards();
  const showHumanIds = usePref('showHumanIds');
  const mobile = isMobile();

  const [data, setData] = useState(null);
  const [title, setTitle] = useState(seed.title || '');
  const [desc, setDesc] = useState('');
  const [attrRows, setAttrRows] = useState([]);
  const [origin, setOrigin] = useState({ title: '', desc: '', attrs: [] });
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [linkPicker, setLinkPicker] = useState(false);
  // #511: a failed load() must NOT blank desc/attrRows/origin out from under
  // whatever the seed/last-good state carried — that would let a later Save
  // silently PUT blanks over real server data. Track the failure instead so
  // the UI can show a retry banner and gate Save until a load succeeds.
  const [loadError, setLoadError] = useState(false);
  // F-21 / REQ-6107 (#581, F2): this card's own approval-rollup state — a SEPARATE
  // best-effort fetch of the SAME batch endpoint the tree dot uses (with a single
  // id), scoped to THIS one open card so it never becomes a per-list-row cost
  // (unlike the hover mini-preview in ItemCardLayer.jsx, this window already does
  // its own one-off `getItem` fetch, so one more bounded request is in keeping).
  // `null` while loading; a FAILURE is `approvalUnknown` below, not silence.
  const [approval, setApproval] = useState(null);
  // True when the approval fetch FAILED — distinct from "loaded, and there is none".
  const [approvalUnknown, setApprovalUnknown] = useState(false);
  const descRef = useRef(null);
  const leaveTimer = useRef(null);
  // #516: capture whatever had focus when this window mounted (the tooltip/id
  // chip trigger) so we can restore it on close — a bespoke floating window
  // gets none of MUI Dialog's built-in focus-restore for free.
  const openerRef = useRef(typeof document !== 'undefined' ? document.activeElement : null);

  const itemId = seed.id || seed.display_id;

  const load = useCallback(async () => {
    try {
      // { throwOnError: true }: getItem() otherwise swallows a fetch failure
      // and resolves with a synthetic "Error loading item" placeholder — which
      // would sail straight through as if it were real data (see the comment
      // on `loadError` above and api.js's getItem for the full story).
      const d = await requirementsApi.getItem(itemId, { throwOnError: true });
      setData(d);
      // #943: attributesToRows itself returns the plain {name,type,value,...}
      // shape its own round-trip tests assert exactly — `_key` is stamped on
      // here, in the caller, so it never appears in that assertion or in the
      // rowsToAttributes(...) payload (which reads only the fields it knows).
      // A stable per-row identity (not the array index) is what `key=` below
      // needs: attrRows is spliced/appended (add/remove a row), and each row
      // holds its own name/value <input>s — an index key would let React
      // reuse a DOM input across a delete, keeping focus/composition state on
      // the WRONG row after a mid-list removal.
      const rows = attributesToRows(d.attributes).map((r) => ({ ...r, _key: uuidv4() }));
      setTitle(d.title || '');
      setDesc(d.description || '');
      setAttrRows(rows);
      setOrigin({ title: d.title || '', desc: d.description || '', attrs: rows });
      setLoadError(false);
    } catch {
      // Deliberately do not touch desc/attrRows/origin/title here — see the
      // comment on `loadError` above.
      setLoadError(true);
    }
  }, [itemId]);
  useEffect(() => { load(); }, [load]);

  // F-21 / REQ-6107 (#581, F2): fetch this item's approval rollup once per
  // itemId — independent of `load()`'s try/catch so a failure here never
  // affects edit/save (best-effort, the dot just stays absent).
  useEffect(() => {
    let live = true;
    setApproval(null);
    setApprovalUnknown(false);
    if (!itemId) return undefined;
    requirementsApi.getApprovalStates(itemId)
      .then((res) => { if (live) setApproval((res && res[itemId]) || null); })
      // A FAILED fetch is not "this item has no approval" (F-21 / #581, named on #711
      // and swept 2026-07-28). This used to render nothing — and nothing here is a
      // CLAIM: the card shows a dot for every other state, so its absence reads as
      // "no sign-off recorded", a domain answer manufactured from a network error.
      // Unlike an empty tree, a missing dot is not self-evidently broken, which is
      // exactly what makes this one worth an explicit indicator rather than silence.
      .catch(() => { if (live) setApprovalUnknown(true); });
    return () => { live = false; };
  }, [itemId]);

  // #516: Escape closes the card (ARIA APG dialog behavior); restore focus to
  // whatever opened it on unmount, if that element is still in the DOM.
  useEffect(() => {
    // Snapshot the ref's value now (mount time) rather than reading
    // `openerRef.current` inside the cleanup closure — it never changes after
    // mount here, but this keeps the cleanup independent of the ref's mutable
    // box, per react-hooks/exhaustive-deps.
    const opener = openerRef.current;
    return () => {
      if (opener && typeof opener.focus === 'function' && document.body.contains(opener)) {
        opener.focus();
      }
    };
  }, []);
  const onKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      closeWindow(key);
    }
  }, [closeWindow, key]);

  // Auto-grow the description to its content up to a cap (the `rows` attr only
  // counts newlines, so a long wrapped paragraph was clamped to 2 lines).
  useEffect(() => {
    const el = descRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const cap = mobile ? Math.round(window.innerHeight * 0.4) : 220;
    el.style.height = `${Math.min(el.scrollHeight + 2, cap)}px`;
  }, [desc, data, mobile]);

  const dirty = title !== origin.title || desc !== origin.desc
    || JSON.stringify(attrRows) !== JSON.stringify(origin.attrs);

  const pin = useCallback(() => { if (!pinned) pinWindow(key); }, [pinned, pinWindow, key]);

  const save = async () => {
    setBusy(true);
    try {
      // #572: PUT now requires a `version` precondition — carry the one from
      // the last successful load() (data), the freshest version this window has seen.
      // #579: a quick card can sit open for a long time, so it is the flow MOST likely
      // to meet a real 409 — and it had no error surface at all (the catch-less try
      // only reset `busy`, so a rejected save just looked like nothing happened).
      // Offer reload / keep-mine; the load() below refreshes the card either way.
      await saveWithConflictPrompt(itemId, {
        title, description: desc, attributes: rowsToAttributes(attrRows),
        version: data && data.version,
      }, { label: (data && (data.display_id || data.title)) || itemId });
      await load();
    } finally { setBusy(false); }
  };
  const del = async () => {
    setBusy(true);
    try { await requirementsApi.deleteItem(itemId, 'deleted from quick card'); closeWindow(key); }
    finally { setBusy(false); }
  };
  const addLink = async (targetId) => {
    setLinkPicker(false);
    if (!targetId) return;
    setBusy(true);
    try { await requirementsApi.createLink(itemId, targetId, 'related'); await load(); }
    finally { setBusy(false); }
  };

  // --- drag (pinned desktop only) -----------------------------------------
  const onDragStart = (e) => {
    if (mobile || !pinned) return;
    focusWindow(key);
    const startX = e.clientX, startY = e.clientY, ox = x, oy = y;
    const onMove = (ev) => {
      moveWindow(key,
        Math.max(4, Math.min(window.innerWidth - 120, ox + (ev.clientX - startX))),
        Math.max(4, Math.min(window.innerHeight - 60, oy + (ev.clientY - startY))));
    };
    const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); document.body.style.userSelect = ''; };
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // --- resize (pinned desktop only, any edge/corner) ----------------------
  const winRef = useRef(null);
  const onResizeStart = (dir) => (e) => {
    if (mobile || !pinned) return;
    e.stopPropagation();
    e.preventDefault();
    focusWindow(key);
    const startX = e.clientX, startY = e.clientY;
    const startW = w || winRef.current?.offsetWidth || 450;
    const startH = h || winRef.current?.offsetHeight || 300;
    const ox = x, oy = y;
    const onMove = (ev) => {
      const dx = ev.clientX - startX, dy = ev.clientY - startY;
      let nw = startW, nh = startH, nx = ox, ny = oy;
      if (dir.includes('e')) nw = Math.max(MIN_W, startW + dx);
      if (dir.includes('s')) nh = Math.max(MIN_H, startH + dy);
      if (dir.includes('w')) {
        nw = Math.max(MIN_W, startW - dx);
        nx = ox + (startW - nw);
      }
      if (dir.includes('n')) {
        nh = Math.max(MIN_H, startH - dy);
        ny = oy + (startH - nh);
      }
      nw = Math.min(nw, window.innerWidth - 16);
      nh = Math.min(nh, window.innerHeight - 16);
      resizeWindow(key, { w: nw, h: nh, x: nx, y: ny });
    };
    const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); document.body.style.userSelect = ''; };
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // Preview (unpinned, desktop) dismisses when the pointer leaves it.
  const onWinLeave = () => {
    if (mobile || pinned) return;
    leaveTimer.current = setTimeout(() => closeWindow(key), 180);
  };
  const onWinEnter = () => { if (leaveTimer.current) { clearTimeout(leaveTimer.current); leaveTimer.current = null; } };

  const eff = data || seed;
  const ids = resolveIdDisplay(eff, showHumanIds);
  const st = statusOf(eff);
  const type = eff.type || eff.attributes?.type || 'requirement';
  const rels = Array.isArray(data?.relationships) ? data.relationships : [];

  const cardZ = itemCardZ(zRank != null ? zRank : z);
  const frame = mobile
    ? { position: 'fixed', inset: '0 0 0 0', display: 'flex', alignItems: 'flex-end', zIndex: cardZ }
    : { position: 'fixed', left: x, top: y, zIndex: cardZ };

  return (
    <>
      {/* #793 H: decorative click-catcher — no content, and the keyboard path is the
          dialog's own Escape handler (#516, below), so it stays out of the a11y tree
          rather than becoming a silent tab stop. */}
      {mobile && <div className="ic-scrim" onClick={() => closeWindow(key)} aria-hidden="true" />}
      {/* #793 H/#149: pure positioning frame. Raising z-order on mousedown is a
          POINTER affordance whose keyboard equivalent already exists — the inner
          dialog's `onFocusCapture` pins the card when focus enters it.
          `role="presentation"` says the wrapper itself carries no semantics; the
          child dialog keeps its own. Escape-to-close (`onKeyDown`) and the
          hover-preview-dismiss (`onMouseEnter`/`onMouseLeave`) live HERE rather than
          on the `role="dialog"` child below — jsx-a11y/no-noninteractive-element-
          interactions treats `dialog` as a non-interactive container role (its own
          docs recommend exactly this: move handlers to a `role="presentation"`
          wrapper) — and `frame` has no size of its own beyond `ic-win`, so the hit
          region is unchanged. Keydown still reaches this handler by bubbling from
          whatever inside the dialog has focus; role has no effect on DOM bubbling. */}
      <div
        style={frame}
        role="presentation"
        onMouseDown={() => pinned && focusWindow(key)}
        onKeyDown={onKeyDown}
        onMouseEnter={onWinEnter}
        onMouseLeave={onWinLeave}
      >
        <div
          ref={winRef}
          className={`ic-win${mobile ? ' ic-win-mobile' : ''}${pinned ? '' : ' ic-win-preview'}`}
          role="dialog" aria-label={`${ids.primary} details`}
          tabIndex={-1}
          style={!mobile ? {
            maxHeight: `calc(100vh - ${y + 12}px)`,
            width: w || undefined,
            height: h || undefined,
            position: 'relative',
          } : undefined}
          onFocusCapture={pin}
        >
          {/* Header / drag handle (drag only once pinned).
              #793 H: `role="presentation"` — the header is a layout row whose children
              (id chip, badges, buttons) keep their own semantics; the mousedown is the
              pointer-only DRAG affordance, not an activation, so there is no keyboard
              behaviour being withheld from anyone. */}
          <div className={`ic-head${pinned && !mobile ? ' ic-head-drag' : ''}`} role="presentation" onMouseDown={onDragStart}>
            {pinned && !mobile && <span className="ic-grip" title="Drag" aria-hidden>⠿</span>}
            <span className="idchip" title={ids.secondary || ''}>{stripIdPrefix(ids.primary)}</span>
            <span className="badge">{type}</span>
            {/* #801: the status dot gets its own handle. `.ic-dot` is worn by THREE
                indicators in this header (status, approval, approval-unknown), and the
                last two mount only when their rollup fetch lands — so a selector on the
                class alone matched one element or two depending on which fetch won the
                race, and threw a strict-mode violation once the approval dot arrived.
                Both approval dots already carried a test id; the status dot did not. */}
            <span className="ic-dot" data-testid="ic-status-dot" title={`Status: ${st.label}`} style={{ background: st.tone }} />
            {/* F-21/F-23 (#581 F2, #607): a SEPARATE approval indicator — this used
                to be conflated with (or entirely absent from) the generic status
                dot above. Glyph + aria-label, not colour alone. */}
            {approval && (() => {
              // F-11 / REQ-6101: `stale_state` keeps a lapsed REJECTION from reading
              // as "Approved · stale" on this card.
              const tone = approvalToneOf(approval.state, approval.stale_state);
              // F-61 / REQ-6119 (#606, D-10, #629): same annotation as the tree dot /
              // Breakdown row — the state itself is unchanged (the approval STANDS).
              const label = approval.reviewer_inactive ? `${tone.label} · user inactive` : tone.label;
              return (
                <span
                  className="ic-dot"
                  role="img"
                  aria-label={`Approval: ${label}`}
                  title={`Approval: ${label}`}
                  data-testid="ic-approval-dot"
                  style={{
                    background: tone.tone, display: 'inline-flex', alignItems: 'center',
                    justifyContent: 'center', fontSize: 7, fontWeight: 900, color: '#fff',
                  }}
                >
                  {tone.glyph}
                </span>
              );
            })()}
            {/* The approval state could not be READ. Rendered as its own indicator so
                the reader is not left to infer "not approved" from an absent dot —
                same three-way distinction ApprovalTrailVerdict draws between intact,
                broken and could-not-determine. Neutral tone and a "?" glyph, never an
                approval colour, so it cannot be mistaken for a decision. */}
            {approvalUnknown && (
              <span
                className="ic-dot"
                role="img"
                aria-label="Approval: could not be loaded"
                title="Approval: could not be loaded — reopen the card to retry"
                data-testid="ic-approval-unknown"
                style={{
                  background: 'var(--text-3, #888)', display: 'inline-flex',
                  alignItems: 'center', justifyContent: 'center', fontSize: 7,
                  fontWeight: 900, color: '#fff',
                }}
              >
                ?
              </span>
            )}
            <div style={{ flex: 1 }} />
            {!pinned && !mobile && (
              <button type="button" className="icon-btn" title="Pin (keep open & drag)" aria-label="Pin" onClick={pin}>📌</button>
            )}
            <button type="button" className="icon-btn" title="Open full page"
              onClick={() => { navigate(`/item/${itemId}`); closeWindow(key); }} aria-label="Open full page">↗</button>
            <button type="button" className="icon-btn" title="Close" onClick={() => closeWindow(key)} aria-label="Close">✕</button>
          </div>

          <div className="ic-body">
            {loadError && (
              <div className="ic-load-error" role="alert" style={{
                display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8,
                padding: '6px 8px', borderRadius: 4, background: 'rgba(192,57,43,0.12)',
                color: '#c0392b', fontSize: 12.5,
              }}>
                <span style={{ flex: 1 }}>
                  Couldn&apos;t load this item&apos;s current data — editing is disabled
                  until it loads, so nothing gets overwritten.
                </span>
                <button type="button" className="btn ghost sm" onClick={load}>Retry</button>
              </div>
            )}
            <div className="ic-lab">Title</div>
            <input className="ic-field" value={title} onChange={(e) => setTitle(e.target.value)} />

            <div className="ic-lab">Description</div>
            <textarea ref={descRef} className="ic-field" value={desc}
              onChange={(e) => setDesc(e.target.value)} style={{ resize: 'none', overflow: 'auto' }} />

            <div className="ic-lab">Attributes <button type="button" className="ic-add" onClick={() => { pin(); setAttrRows([...attrRows, { name: '', type: 'string', value: '', _key: uuidv4() }]); }}>＋ add</button></div>
            {attrRows.length === 0 && <div className="t-body muted3" style={{ fontSize: 12 }}>No attributes.</div>}
            {attrRows.map((r, i) => (
              <div key={r._key} className="ic-attr">
                <input className="ic-field sm" placeholder="name" value={r.name}
                  onChange={(e) => setAttrRows(attrRows.map((x2, j) => j === i ? { ...x2, name: e.target.value } : x2))} />
                <input className="ic-field sm" placeholder="value" value={r.value}
                  onChange={(e) => setAttrRows(attrRows.map((x2, j) => j === i ? { ...x2, value: e.target.value } : x2))} />
                <button type="button" className="icon-btn" aria-label="Remove attribute" onClick={() => setAttrRows(attrRows.filter((_, j) => j !== i))}>✕</button>
              </div>
            ))}

            <div className="ic-lab">Links <button type="button" className="ic-add" onClick={() => { pin(); setLinkPicker(true); }}>＋ add link</button></div>
            {rels.length === 0 && <div className="t-body muted3" style={{ fontSize: 12 }}>No links.</div>}
            {rels.map((rel) => {
              const relItem = { id: rel.id, display_id: rel.display_id, title: rel.title, type: rel.type };
              const relIds = resolveIdDisplay({ display_id: rel.display_id, id: rel.id }, showHumanIds);
              return (
                <div key={`${rel.id}-${rel.link_type}`} className="ic-link">
                  <span className="t-over" style={{ minWidth: 64 }}>{rel.link_type || 'related'}</span>
                  {/* Recursive: the linked id is itself a tooltip trigger. */}
                  <ItemTooltip item={relItem}>
                    <span className="idchip" title={relIds.secondary || ''}>{stripIdPrefix(relIds.primary)}</span>
                  </ItemTooltip>
                  <span className="grow" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12.5 }}>{rel.title || ''}</span>
                </div>
              );
            })}
          </div>

          <div className="ic-actions">
            {confirmDel ? (
              <>
                <span className="t-body" style={{ fontSize: 12.5 }}>Delete this item?</span>
                <div style={{ flex: 1 }} />
                <button type="button" className="btn ghost sm" onClick={() => setConfirmDel(false)}>Cancel</button>
                <button type="button" className="btn sm" style={{ color: '#c0392b' }} disabled={busy} onClick={del}>Delete</button>
              </>
            ) : (
              <>
                <button type="button" className="btn ghost sm" title="Delete" onClick={() => { pin(); setConfirmDel(true); }}>🗑 Delete</button>
                <div style={{ flex: 1 }} />
                {dirty && <button type="button" className="btn ghost sm" onClick={() => { setTitle(origin.title); setDesc(origin.desc); setAttrRows(origin.attrs); }}>Cancel</button>}
                <button type="button" className="btn primary sm" disabled={!dirty || busy || loadError} title={loadError ? 'Load failed — retry before saving' : undefined} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
              </>
            )}
          </div>
        </div>
        {/* #844: siblings of `.ic-win`, not children of it. `.ic-win` sets
            `overflow: hidden` (it clips scrolled body content and follows its own
            `border-radius`), and every corner/edge handle is offset OUTSIDE `.ic-win`'s
            own box on purpose (e.g. `se`: `bottom:-4,right:-4` on a 12x12 box, so only
            an ~8x8 sliver sits inside the boundary — smaller still at a ROUNDED corner,
            where the border-radius clip carves further into that sliver). A handle's
            `getBoundingClientRect()` always reports its full nominal box regardless of
            an ancestor's clip, so a click aimed at that box's geometric centre lands
            within a couple of CSS px of the clip edge — sub-pixel layout rounding is
            all it takes to land the centre in the clipped, non-hit-testable remainder,
            and the mousedown silently does nothing (no listener ever attaches, so the
            whole drag is a no-op start to finish). `frame` (this element's parent) has
            no `overflow` of its own and already establishes the handles' containing
            block (`position:absolute` resolves against the nearest positioned
            ancestor, and `frame` is `position:fixed`) — `.ic-win` is its only
            NORMAL-FLOW child, so `frame`'s shrink-to-fit box is identical to
            `.ic-win`'s, and an `absolute` sibling here computes to the exact same
            pixels as before, just outside the clip. #149's keyboard-resize gap and the
            #793 a11y note on these handles are unchanged by the move. */}
        {pinned && !mobile && EDGES.map(({ dir, cursor, style }) => (
          <div key={dir} className="ic-resize" style={{ ...style, cursor }} aria-hidden="true" onMouseDown={onResizeStart(dir)} />
        ))}
      </div>

      {/* #375: scope the relationship-target tree to THIS item's project — a
          relationship can only be formed within a project, so offering other
          projects' items is both wrong and a cross-project exposure surface. */}
      <UnifiedContainerSelector open={linkPicker} onClose={() => setLinkPicker(false)}
        onSelect={(id) => addLink(id)} title="Link to item" selectMode="all"
        projectId={data?.project_id || seed?.project_id || null} fallbackToLegacy />
    </>
  );
}

ItemCardWindow.propTypes = { win: PropTypes.object.isRequired };
